import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/project-cooperation.css';
import useRevealAnimation from '../hooks/useRevealAnimation';
import useDocumentTitle from '../hooks/useDocumentTitle';
import {
  fetchProjectCooperationDetail,
  fetchProjectCooperationList,
  ProjectCooperationApiError,
  type ProjectCooperationEntry,
} from './projectCooperationApi';

type SupportedLocale = 'zh' | 'en' | 'ja';

const normalizeLanguage = (language?: string | null): SupportedLocale => {
  const normalized = (language ?? '').toLowerCase();
  if (normalized.startsWith('zh')) return 'zh';
  if (normalized.startsWith('ja')) return 'ja';
  return 'en';
};

const ProjectCooperation: React.FC = () => {
  const { i18n, t } = useTranslation();
  const pageRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<ProjectCooperationEntry[]>([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const params = useParams<{ slug?: string }>();

  const locale = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  useRevealAnimation(pageRef);

  useEffect(() => {
    let cancelled = false;

    const loadItems = async () => {
      setLoading(true);
      setError('');
      try {
        const results = await fetchProjectCooperationList(locale);
        if (cancelled) return;
        setItems(results);

        const routeSlug = params.slug ?? '';
        const matchedFromList = results.find((item) => item.slug === routeSlug);
        if (matchedFromList) {
          setSelectedSlug(matchedFromList.slug);
          return;
        }

        if (routeSlug) {
          try {
            const detail = await fetchProjectCooperationDetail(routeSlug, locale);
            if (cancelled) return;
            setSelectedSlug(detail.slug);
            if (!results.some((item) => item.slug === detail.slug)) {
              setItems((current) => [detail, ...current]);
            }
            return;
          } catch {
            if (cancelled) return;
          }
        }

        const defaultItem = results.find((item) => item.is_default_display) ?? results[0];
        setSelectedSlug(defaultItem?.slug || '');
      } catch (loadError) {
        if (cancelled) return;
        setItems([]);
        setSelectedSlug('');
        setError(loadError instanceof ProjectCooperationApiError || loadError instanceof Error ? loadError.message : t('ProjectCooperation.status.errorDescription'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadItems();
    return () => {
      cancelled = true;
    };
  }, [locale, params.slug, t]);

  const selectedItem = useMemo(() => {
    if (!items.length) return null;
    return items.find((item) => item.slug === selectedSlug) ?? items[0];
  }, [items, selectedSlug]);
  useDocumentTitle(selectedItem?.title || t('ProjectCooperation.hero.title'));

  return (
    <div className="project-cooperation-page" ref={pageRef}>
      <section className="project-cooperation-hero">
        <div className="project-cooperation-hero-bg">
          <div className="project-cooperation-grid" />
          <div className="project-cooperation-glow" />
        </div>
        <div className="section-container project-cooperation-hero-content">
          <div className="project-cooperation-hero-copy reveal">
            <span className="project-cooperation-kicker">{t('ProjectCooperation.hero.kicker')}</span>
            <h1 className="project-cooperation-hero-title">{t('ProjectCooperation.hero.title')}</h1>
            <p className="project-cooperation-hero-description">{t('ProjectCooperation.hero.description')}</p>
          </div>
        </div>
      </section>

      <section className="section-container project-cooperation-main-section">
        {loading ? <div className="project-cooperation-status-card reveal">{t('ProjectCooperation.status.loading')}</div> : null}

        {!loading && error ? (
          <div className="project-cooperation-status-card is-error reveal">
            <h3>{t('ProjectCooperation.status.errorTitle')}</h3>
            <p>{t('ProjectCooperation.status.errorDescription')}</p>
          </div>
        ) : null}

        {!loading && !error && !selectedItem ? (
          <div className="project-cooperation-status-card reveal">
            <h3>{t('ProjectCooperation.status.emptyTitle')}</h3>
            <p>{t('ProjectCooperation.status.emptyDescription')}</p>
          </div>
        ) : null}

        {!loading && !error && selectedItem ? (
          <>
            <section className="project-cooperation-switcher-panel reveal">
              <div className="section-header">
                <span className="section-subtitle">{t('ProjectCooperation.switcher.label')}</span>
                <h2 className="section-title">{t('ProjectCooperation.switcher.title')}</h2>
              </div>
              <div className="project-cooperation-switcher" role="tablist" aria-label={t('ProjectCooperation.switcher.title')}>
                {items.map((item) => {
                  const active = selectedItem.slug === item.slug;
                  return (
                    <button
                      key={item.slug}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      className={`project-cooperation-chip${active ? ' is-active' : ''}`}
                      onClick={() => setSelectedSlug(item.slug)}
                    >
                      <span>{item.badge}</span>
                      <div className="project-cooperation-chip-copy">
                        <strong>{item.title}</strong>
                        <small>{item.summary}</small>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <div className="project-cooperation-layout">
              <div className="project-cooperation-content">
                <section className="project-cooperation-card reveal delay-1">
                  <span className="project-cooperation-page-tag">{selectedItem.badge}</span>
                  <h2>{selectedItem.title}</h2>
                  <p>{selectedItem.summary}</p>
                  <p className="project-cooperation-meta">
                    <span>{t('ProjectCooperation.meta.publishedAt')}</span>
                    <strong>{selectedItem.published_at}</strong>
                  </p>

                  <div className="project-cooperation-sections">
                    <section className="project-cooperation-rich-section">
                      <div className="section-header">
                        <span className="section-subtitle">{t('ProjectCooperation.overview.label')}</span>
                        <h3 className="section-title">{t('ProjectCooperation.overview.title')}</h3>
                      </div>
                      <div className="project-cooperation-paragraph-stack">
                        {selectedItem.body.map((paragraph, index) => (
                          <p key={`${selectedItem.slug}-paragraph-${index}`}>{paragraph}</p>
                        ))}
                      </div>
                    </section>

                    <section className="project-cooperation-rich-section">
                      <div className="section-header">
                        <span className="section-subtitle">{t('ProjectCooperation.content.label')}</span>
                        <h3 className="section-title">{t('ProjectCooperation.content.title')}</h3>
                      </div>
                      <div className="project-cooperation-item-grid">
                        {selectedItem.cooperation_items.map((item, index) => (
                          <article className="project-cooperation-item-card reveal" style={{ transitionDelay: `${0.08 * (index + 1)}s` }} key={`${selectedItem.slug}-${item.index}-${item.title}`}>
                            <div className="project-cooperation-item-index">{item.index}</div>
                            <div className="project-cooperation-item-copy">
                              <h3>{item.title}</h3>
                              <p>{item.description}</p>
                            </div>
                          </article>
                        ))}
                      </div>
                    </section>
                  </div>
                </section>
              </div>

              <aside className="project-cooperation-questionnaire-panel reveal delay-2">
                <span className="project-cooperation-questionnaire-label">{t('ProjectCooperation.questionnaire.label')}</span>
                <h3>{selectedItem.questionnaire_title || t('ProjectCooperation.questionnaire.title')}</h3>
                <p className="project-cooperation-questionnaire-description">
                  {selectedItem.questionnaire_description[0] || t('ProjectCooperation.questionnaire.description')}
                </p>

                <div className="project-cooperation-questionnaire-copy">
                  {selectedItem.questionnaire_description.slice(1).map((paragraph, index) => (
                    <p key={`${selectedItem.slug}-questionnaire-${index}`}>{paragraph}</p>
                  ))}
                </div>

                <div className="project-cooperation-qr-block" aria-label={t('ProjectCooperation.questionnaire.qrLabel')}>
                  <div className="project-cooperation-qr-visual">
                    {selectedItem.qr_image_url ? (
                      <img src={selectedItem.qr_image_url} alt={t('ProjectCooperation.questionnaire.qrLabel')} className="project-cooperation-qr-image" />
                    ) : (
                      <span>{t('ProjectCooperation.questionnaire.qrEmpty')}</span>
                    )}
                  </div>
                  <div className="project-cooperation-qr-copy">
                    <strong>{t('ProjectCooperation.questionnaire.qrLabel')}</strong>
                    <span>{selectedItem.qr_hint || t('ProjectCooperation.questionnaire.qrEmpty')}</span>
                  </div>
                </div>
              </aside>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

export default ProjectCooperation;



